import React, { createContext, useState, useContext, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";

// Define types for our upload items
export interface UploadItem {
  id: string; // Unique ID for this upload task
  type: "album" | "photo"; // Type of upload
  title: string; // Display name
  progress: number; // 0-100
  status: "pending" | "uploading" | "processing" | "success" | "error";
  totalFiles: number;
  completedFiles: number;
  albumId?: string; // For album uploads
  startTime: number;
  estimatedTimeRemaining?: number;
  error?: string;
}

interface UploadContextType {
  uploads: UploadItem[];
  addUpload: (upload: Omit<UploadItem, "id">) => string;
  updateUpload: (id: string, updates: Partial<UploadItem>) => void;
  removeUpload: (id: string) => void;
  hasActiveUploads: boolean;
}

// Create the context with a default value
const UploadContext = createContext<UploadContextType | undefined>(undefined);

// Provider component
export const UploadProvider = ({ children }: { children: React.ReactNode }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const { toast } = useToast();
  const [hasActiveUploads, setHasActiveUploads] = useState(false);

  // Check for active uploads
  useEffect(() => {
    const activeUploads = uploads.filter(
      (upload) =>
        upload.status === "uploading" || upload.status === "processing"
    );
    setHasActiveUploads(activeUploads.length > 0);
  }, [uploads]);

  // Add a new upload task
  const addUpload = (upload: Omit<UploadItem, "id">) => {
    // Generate a unique ID for this upload
    const id = uuidv4();

    // Add to uploads list
    const newUpload: UploadItem = {
      ...upload,
      id,
    };

    setUploads((prevUploads) => [...prevUploads, newUpload]);

    // Show toast notification for new upload
    toast({
      title: `${upload.type === "album" ? "Album" : "Photo"} upload started`,
      description: `${upload.title} is now uploading in the background.`,
      duration: 3000,
    });

    return id;
  };

  // Update an existing upload task
  const updateUpload = (id: string, updates: Partial<UploadItem>) => {
    setUploads((prevUploads) =>
      prevUploads.map((upload) => {
        if (upload.id === id) {
          // Create an updated upload object
          const updatedUpload = {
            ...upload,
            ...updates,
          };

          // Handle transition to success/error status
          if (updates.status === "success" && upload.status !== "success") {
            toast({
              title: "Upload Complete",
              description: `${upload.title} has been uploaded successfully.`,
              duration: 3000,
            });
          } else if (updates.status === "error" && upload.status !== "error") {
            toast({
              variant: "destructive",
              title: "Upload Failed",
              description: updates.error || `${upload.title} upload failed.`,
              duration: 5000,
            });
          }

          return updatedUpload;
        }
        return upload;
      })
    );
  };

  // Remove an upload task
  const removeUpload = (id: string) => {
    // Create a local variable to track if we should remove the upload
    let shouldRemove = false;
    let uploadTitle = "";

    setUploads((prevUploads) => {
      const uploadToRemove = prevUploads.find((upload) => upload.id === id);

      // Only allow removing completed or failed uploads
      if (
        uploadToRemove &&
        (uploadToRemove.status === "success" ||
          uploadToRemove.status === "error")
      ) {
        shouldRemove = true;
        uploadTitle = uploadToRemove.title;
        return prevUploads.filter((upload) => upload.id !== id);
      }

      // If trying to remove an active upload, show warning toast
      if (
        uploadToRemove &&
        (uploadToRemove.status === "uploading" ||
          uploadToRemove.status === "processing")
      ) {
        toast({
          variant: "destructive",
          title: "Cannot Remove Active Upload",
          description: "Please wait for the upload to complete or fail.",
          duration: 3000,
        });
      }

      return prevUploads;
    });

    // If we removed a successful upload, show a toast confirming its dismissal
    if (shouldRemove) {
      toast({
        title: "Upload Dismissed",
        description: `${uploadTitle} has been removed from notifications.`,
        duration: 2000,
      });
    }
  };

  return (
    <UploadContext.Provider
      value={{
        uploads,
        addUpload,
        updateUpload,
        removeUpload,
        hasActiveUploads,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

// Custom hook to use the upload context
export const useUpload = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
